import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [teamId, setTeamId] = useState(null);
  const [lockPassed, setLockPassed] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async ({ keepOnError = false } = {}) => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTeamId(data.team_id);
      setLockPassed(data.lock_passed);
      setImpersonating(!!data.impersonating);
    } catch {
      // Not authenticated (no/expired session cookie). On initial load we clear state; but
      // right after an explicit login we keep the optimistic user so a transient /auth/me
      // failure (CDN latency, cold start) doesn't bounce a just-verified user back to OTP.
      if (!keepOnError) { setUser(null); setTeamId(null); setImpersonating(false); }
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/config");
      setConfig(data);
    } catch (err) {
      console.error("Failed to load app config:", err);
    }
  }, []);

  useEffect(() => {
    (async () => { await loadConfig(); await refresh(); setLoading(false); })();
  }, [loadConfig, refresh]);

  const login = useCallback((userObj) => {
    // Session is established via an httpOnly cookie set by the backend; hydrate state now
    // and enrich in the background — keep the verified user even if that refresh transiently fails.
    setUser(userObj);
    refresh({ keepOnError: true });
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* clear locally regardless */ }
    setUser(null); setTeamId(null); setImpersonating(false);
  }, []);

  const stopImpersonating = useCallback(async () => {
    await api.post("/auth/stop-impersonating");
    setImpersonating(false);
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ user, teamId, lockPassed, impersonating, config, loading, login, logout, refresh, loadConfig, stopImpersonating }),
    [user, teamId, lockPassed, impersonating, config, loading, login, logout, refresh, loadConfig, stopImpersonating]
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
